import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { map, Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CartService } from 'src/app/services/cart/cart.service';

interface BottomNavItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  action?: () => void;
}

@Component({
  selector: 'app-bottom-nav',
  templateUrl: './bottom-nav.component.html',
  styleUrls: ['./bottom-nav.component.scss'],
})
export class BottomNavComponent implements OnInit, OnDestroy {
  navItems: BottomNavItem[] = [];
  currentUrl: string = '';
  readonly cartCount$: Observable<number>;
  private routerSub?: Subscription;
  private langSub?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly authService: AuthService,
    private readonly cartService: CartService
  ) {
    this.cartCount$ = this.cartService.items$.pipe(
      map((items) => items.reduce((sum, item) => sum + item.quantity, 0))
    );
  }

  ngOnInit(): void {
    this.navItems = this.buildNavItems();
    this.currentUrl = this.router.url;
    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.urlAfterRedirects;
      }
    });
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.navItems = this.buildNavItems();
    });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.langSub?.unsubscribe();
  }

  trackByNavItem(_: number, item: BottomNavItem): string {
    return item.id;
  }

  isActive(item: BottomNavItem): boolean {
    const normalizedUrl = this.currentUrl.split('?')[0];
    if (item.id === 'home') {
      return normalizedUrl.endsWith('/home');
    }
    if (item.id === 'profile') {
      return normalizedUrl.includes('/profile');
    }
    return normalizedUrl.includes(item.route);
  }

  handleNavItem(event: Event, item: BottomNavItem): void {
    event.preventDefault();

    if (item.action) {
      item.action();
      return;
    }

    this.router.navigate([item.route]);
  }

  private buildNavItems(): BottomNavItem[] {
    const lang = localStorage.getItem('lang') || 'en';

    return [
      {
        id: 'home',
        label: this.translate.instant('MENU.HOME'),
        icon: 'pi-home',
        route: `/${lang}/home`,
      },
      {
        id: 'categories',
        label: this.translate.instant('APP.NAV_CATEGORIES'),
        icon: 'pi-th-large',
        route: `/${lang}/categories`,
      },
      {
        id: 'shop',
        label: this.translate.instant('APP.NAV_SHOP'),
        icon: 'pi-shopping-bag',
        route: `/${lang}/products`,
      },
      {
        id: 'cart',
        label: this.translate.instant('APP.NAV_CART'),
        icon: 'pi-shopping-cart',
        route: `/${lang}/cart`,
      },
      {
        id: 'profile',
        label: this.translate.instant('APP.NAV_PROFILE'),
        icon: 'pi-user',
        route: `/${lang}/profile`,
        action: () => {
          const latestLang = localStorage.getItem('lang') || 'en';
          if (this.authService.isLoggedIn()) {
            this.router.navigate([`/${latestLang}/profile`]);
            return;
          }
          this.router.navigate([`/${latestLang}/login`]);
        },
      },
    ];
  }
}
